import {
  IRideStep,
  Poi,
  Ride,
  Route,
  TPoiCategoryCode,
  useGeoveloMap,
  usePois,
  useRides,
} from '@geovelo-frontends/commons';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import MapMarkers from '../../components/map-markers';
import { AppContext } from '../../context';

const mapId = 'ride-map';

function Map({
  path,
  ride,
  route,
  returnRoute,
  pois,
  onStepPhotosDialogOpen,
}: {
  onStepPhotosDialogOpen: (step: IRideStep) => void;
  path: string;
  pois: Poi[] | undefined;
  ride: Ride | undefined;
  returnRoute: Route | null | undefined;
  route: Route | null | undefined;
}): React.JSX.Element {
  const [selectedPoiCategories, selectPoiCategories] =
    useState<{ [key in TPoiCategoryCode]?: boolean }>();
  const [returnRouteShowed, showReturnRoute] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const {
    poi: { categories: poiCategories },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    initialized: mapInitialized,
    map,
    zoom,
    init: initMap,
    destroy: destroyMap,
  } = useGeoveloMap();
  const {
    initialized: ridesLayersInitialized,
    selectedRideStepId: selectedRideStepOnMap,
    init: initRidesLayers,
    select: updateRidesLayers,
    // selectRideStep: selectRideStepOnMap,
    unselectRideStep: unselectRideStepOnMap,
    clear: clearRidesLayers,
  } = useRides(map, theme, [], undefined, undefined, {
    openStepPhotoDialog: onStepPhotosDialogOpen,
  });
  const {
    initialized: poisInitializedOnMap,
    selectedPoi: selectedPoiOnMap,
    init: initPoisOnMap,
    update: updatePoisOnMap,
    // select: selectPoiOnMap,
    unselect: unselectPoiOnMap,
    clear: clearPoisMap,
  } = usePois(
    map,
    theme,
    false,
    poiCategories,
    poiCategories?.reduce<{ [key in TPoiCategoryCode]?: boolean }>((res, { code }) => {
      res[code] = true;

      return res;
    }, {}) || {},
    undefined,
    { useNewImageMarkers: true, enqueueSnackbar, closeSnackbar },
  );

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (poiCategories) {
      selectPoiCategories({
        ...selectedPoiCategories,
        ...poiCategories
          .find(({ code }) => code === 'toDoToSee')
          ?.children.reduce<{ [key in TPoiCategoryCode]?: boolean }>(
            (res, { code }) => {
              res[code] = true;
              return res;
            },
            { toDoToSee: true },
          ),
      });
    }
  }, [poiCategories]);

  useEffect(() => {
    if (initialized && !mapInitialized) {
      initMap({
        container: mapId,
        customZoomControls: true,
        locateControl: true,
        scaleControl: true,
        scrollZoom: false,
      });
    }

    return () => destroyMap();
  }, [initialized]);

  useEffect(() => {
    if (map && ride) {
      if (!ridesLayersInitialized) initRidesLayers();
      if (!poisInitializedOnMap) initPoisOnMap();
    }

    return () => {
      clearRidesLayers();
      clearPoisMap();
    };
  }, [map, ride]);

  useEffect(() => {
    if (ridesLayersInitialized && ride && route) {
      if (returnRouteShowed) {
        updateRidesLayers(ride, returnRoute, true, true, false);
      } else {
        updateRidesLayers(ride, route, true, false, false);
      }
    }
  }, [ridesLayersInitialized, ride, route, returnRouteShowed]);

  useEffect(() => {
    if (poisInitializedOnMap && pois && pois.length > 0) {
      updatePoisOnMap(pois);
    }
  }, [zoom, poisInitializedOnMap, pois]);

  useEffect(() => {
    if (selectedRideStepOnMap) unselectPoiOnMap();
  }, [selectedRideStepOnMap]);

  useEffect(() => {
    if (selectedPoiOnMap) unselectRideStepOnMap();
  }, [selectedPoiOnMap]);

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box height={400} id={mapId}>
          {!ride && <Skeleton height={400} variant="rectangular" />}
        </Box>
        {(ride?.routeId || ride?.condensedGeometry) &&
          (ride?.returnRouteId || ride?.condensedReturnGeometry) && (
            <Box alignItems="center" display="flex" gap={1} justifyContent="flex-end">
              <Button
                color="primary"
                onClick={() => showReturnRoute(false)}
                variant={returnRouteShowed ? 'outlined' : 'contained'}
              >
                {t('geovelo.rides.detail.outward_route')}
              </Button>
              <Button
                color="primary"
                onClick={() => showReturnRoute(true)}
                variant={returnRouteShowed ? 'contained' : 'outlined'}
              >
                {t('geovelo.rides.detail.return_route')}
              </Button>
            </Box>
          )}
      </Box>
      {selectedPoiCategories && (
        <MapMarkers map={map} path={path} selectedPoiCategories={selectedPoiCategories} />
      )}
    </>
  );
}

export default Map;
